import axios from 'axios';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:5293/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30s — prevents indefinite hangs when backend is unresponsive
});

// Read a cookie by name. Used for the print-scope token cookies set by the
// server-side Chromium renderer (see PrintRenderingService).
function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Request interceptor — attach JWT + Company header.
// On the internal print route (`/print/internal/...`), the server-side
// Chromium renderer sets short-lived cookies `__print_jwt` and
// `__print_company` that the SPA must use for its data calls — Chromium's
// localStorage is empty so the regular bootstrap path doesn't have credentials.
api.interceptors.request.use(config => {
  // Public share endpoints must NOT carry tenant credentials — the API
  // resolves the company from the share-link row, not from request headers.
  // Sending Authorization/X-Company-Id from a logged-in operator's browser
  // would be ignored server-side but is unnecessary leakage either way.
  const url = config.url || '';
  const isPublicUrl = url.startsWith('/public/') || url.startsWith('public/');
  if (isPublicUrl) return config;

  const onInternalPrint =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/print/internal/');

  let token = null;
  let companyId = null;
  if (onInternalPrint) {
    token = readCookie('__print_jwt') || localStorage.getItem('jwt');
    companyId = readCookie('__print_company') || localStorage.getItem('companyId');
  } else {
    token = localStorage.getItem('jwt');
    companyId = localStorage.getItem('companyId');
  }

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  const hasExplicitCompany = Object.keys(config.headers).some(
    (k) => k.toLowerCase() === 'x-company-id'
  );
  if (companyId && !hasExplicitCompany) {
    config.headers['X-Company-Id'] = companyId;
  }
  return config;
});

// Single in-flight refresh — concurrent 401s share one /auth/refresh call.
let _refreshPromise = null;
function tryRefresh() {
  if (_refreshPromise) return _refreshPromise;
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return Promise.reject(new Error('no refresh token'));
  _refreshPromise = axios
    .post(
      `${api.defaults.baseURL}/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    )
    .then(res => {
      const data = res.data;
      if (data?.token) {
        localStorage.setItem('jwt', data.token);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        } else {
          localStorage.removeItem('refreshToken');
        }
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          localStorage.setItem('userdata', JSON.stringify(data.user));
        }
        if (data.companies && data.companies.length > 0) {
          // Preserve current company selection if still in the list, else pick first.
          const current = localStorage.getItem('companyId');
          const stillValid = current && data.companies.some(c => String(c.id) === String(current));
          if (!stillValid) {
            localStorage.setItem('companyId', String(data.companies[0].id));
          }
          localStorage.setItem('companies', JSON.stringify(data.companies));
        }
      }
      return data;
    })
    .finally(() => {
      _refreshPromise = null;
    });
  return _refreshPromise;
}

function forceLogout() {
  localStorage.removeItem('jwt');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('userdata');
  localStorage.removeItem('companyId');
  localStorage.removeItem('userPermissions');

  // Signal all SSE/EventSource connections to close before redirect
  window.dispatchEvent(new Event('auth:logout'));

  if (window.location.pathname.startsWith('/mobile/')) {
    window.location.href = '/mobile/login';
  } else {
    window.location.href = '/login';
  }
}

// Response interceptor — try refresh on 401, fall through to logout.
api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config || {};
    const status = error.response && error.response.status;
    if (status === 401 && !original._retry && !(original.url || '').includes('/auth/refresh')) {
      original._retry = true;
      try {
        const data = await tryRefresh();
        if (data?.token) {
          original.headers = original.headers || {};
          original.headers['Authorization'] = `Bearer ${data.token}`;
          return api(original);
        }
      } catch (_) {
        /* fall through to logout */
      }
      forceLogout();
    }
    return Promise.reject(error);
  }
);

/**
 * Resolves a server-relative upload URL (e.g. "/uploads/tp-pod/1/foo.png") to
 * the backend origin (e.g. "http://localhost:5293/uploads/tp-pod/1/foo.png").
 * Static files are served by the API host, not the Vue dev server.
 */
export function resolveUploadUrl(relative) {
  if (!relative) return relative;
  // Already absolute
  if (/^https?:\/\//i.test(relative)) return relative;
  const apiBase = process.env.VUE_APP_API_BASE_URL || 'http://localhost:5293/api';
  // Strip trailing /api (or /api/) so we land at the backend root.
  const origin = apiBase.replace(/\/?api\/?$/i, '');
  const path = relative.startsWith('/') ? relative : `/${relative}`;
  return `${origin}${path}`;
}

export default api;
